import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

export default function SampleReportPage() {
  useEffect(() => {
    document.title = 'Sample Intelligence Report | CrowdRounds';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'See what a CrowdRounds intelligence report looks like. Full demonstration of our 7-layer success intelligence model.');
    }
  }, []);

  const handleDownload = () => {
    // Track download
    if (window.gtag) {
      window.gtag('event', 'download', {
        event_category: 'Sample Report',
        event_label: 'FlowTrack Intelligence Report'
      });
    }
    
    // Open premium report in new tab
    window.open('https://chatgpt.com/c/69287725-8958-8333-8833-a671b278b2af#:~:text=Download%20Your%20Reports,Download%20CrowdRounds_Premium_Report.pdf', '_blank');
  };

  return (
    <>
      <Navigation />

      <main className="min-h-screen bg-white">
        {/* Header */}
        <section className="bg-gradient-to-br from-teal-600 to-slate-800 text-white py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-5xl font-bold mb-6">CrowdRounds Sample Report</h1>
            <p className="text-xl text-teal-100 mb-8">Intelligence Snapshot — Demonstration Only</p>
            <button
              onClick={handleDownload}
              className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-teal-50 transition-all inline-flex items-center gap-3"
            >
              <i className="ri-download-line text-xl"></i>
              Download This Report
            </button>
          </div>
        </section>

        {/* App Overview */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-slate-50 border-l-4 border-teal-600 p-8 rounded-lg mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">App Overview (Example App: "FlowTrack")</h2>
            <p className="text-slate-700 leading-relaxed">
              This sample illustrates what an actual CrowdRounds report looks like using our multi-layer intelligence model.
              This example is fictional and used only for demonstration.
            </p>
          </div>

          {/* 1. Success Probability Score */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold text-xl">
                1
              </div>
              <h3 className="text-3xl font-bold text-slate-900">Success Probability Score™</h3>
            </div>
            <div className="bg-gradient-to-r from-teal-50 to-slate-50 p-8 rounded-xl mb-6">
              <div className="text-6xl font-bold text-teal-600 mb-4">72 / 100</div>
              <div className="text-2xl font-semibold text-slate-800 mb-4">Moderate to Strong Market Potential</div>
              <p className="text-slate-700 leading-relaxed">
                Your app shows early traction signals and above-average alignment with user expectations. Additional clarity in onboarding and feature positioning would raise this score significantly.
              </p>
            </div>
          </div>

          {/* 2. Category Standards Benchmark */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold text-xl">
                2
              </div>
              <h3 className="text-3xl font-bold text-slate-900">Category Standards Benchmark</h3>
            </div>
            <div className="bg-white border-2 border-slate-200 p-8 rounded-xl">
              <div className="text-xl font-semibold text-slate-800 mb-6">Position: Upper-Mid Tier in Productivity Apps</div>
              
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-teal-600 mb-4 flex items-center gap-2">
                  <i className="ri-arrow-up-circle-fill"></i>
                  You outperform 63% of comparable apps in:
                </h4>
                <ul className="space-y-2 ml-8">
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-1"></i>
                    Feature clarity
                  </li>
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-1"></i>
                    Stability and technical performance
                  </li>
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-1"></i>
                    Core user value proposition
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
                  <i className="ri-arrow-down-circle-fill"></i>
                  But lag behind top performers in:
                </h4>
                <ul className="space-y-2 ml-8">
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1"></i>
                    Onboarding efficiency
                  </li>
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1"></i>
                    Retention UX patterns
                  </li>
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1"></i>
                    Emotional alignment cues
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 3. Competitive Gap Analysis */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold text-xl">
                3
              </div>
              <h3 className="text-3xl font-bold text-slate-900">Competitive Gap Analysis</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-red-50 border-2 border-red-200 p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-red-800 mb-4">Key areas where competitors have an edge:</h4>
                <ul className="space-y-3">
                  <li className="text-slate-700">• Faster first-action flow</li>
                  <li className="text-slate-700">• More streamlined menu hierarchy</li>
                  <li className="text-slate-700">• Clearer visual reinforcement of value during early usage</li>
                  <li className="text-slate-700">• Better expectation-setting during onboarding</li>
                </ul>
              </div>
              <div className="bg-teal-50 border-2 border-teal-200 p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-teal-800 mb-4">Your strongest competitive advantages:</h4>
                <ul className="space-y-3">
                  <li className="text-slate-700">• Strong primary feature</li>
                  <li className="text-slate-700">• Simpler core interface</li>
                  <li className="text-slate-700">• Higher trust indicators</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 4. Feature Strength Index */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold text-xl">
                4
              </div>
              <h3 className="text-3xl font-bold text-slate-900">Feature Strength Index</h3>
            </div>
            <div className="bg-white border-2 border-slate-200 p-8 rounded-xl">
              <div className="mb-8">
                <h4 className="text-xl font-semibold text-teal-600 mb-4">Your Top Strengths</h4>
                <ul className="space-y-3">
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-star-fill text-teal-600 mt-1"></i>
                    Core feature performs above category averages
                  </li>
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-star-fill text-teal-600 mt-1"></i>
                    Low friction in core task execution
                  </li>
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-star-fill text-teal-600 mt-1"></i>
                    Strong "utility clarity" — users quickly understand what the app does
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-slate-800 mb-4">Improvement Opportunities</h4>
                <ul className="space-y-3">
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-lightbulb-line text-amber-600 mt-1"></i>
                    Add contextual hints to reduce cognitive load
                  </li>
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-lightbulb-line text-amber-600 mt-1"></i>
                    Remove 2 redundant steps from onboarding
                  </li>
                  <li className="text-slate-700 flex items-start gap-2">
                    <i className="ri-lightbulb-line text-amber-600 mt-1"></i>
                    Add micro-interactions to enhance user confidence
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 5. Emotional Response Snapshot */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold text-xl">
                5
              </div>
              <h3 className="text-3xl font-bold text-slate-900">Emotional Response Snapshot</h3>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-teal-50 p-8 rounded-xl">
              <p className="text-slate-700 mb-6">Predicted user emotional responses during first 10 minutes:</p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <span className="text-slate-700 font-semibold w-32">Value:</span>
                  <div className="flex-1 bg-white rounded-full h-8 overflow-hidden">
                    <div className="bg-teal-600 h-full flex items-center justify-end pr-3" style={{width: '80%'}}>
                      <span className="text-white font-semibold text-sm">8/10</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-slate-700 font-semibold w-32">Clarity:</span>
                  <div className="flex-1 bg-white rounded-full h-8 overflow-hidden">
                    <div className="bg-amber-500 h-full flex items-center justify-end pr-3" style={{width: '60%'}}>
                      <span className="text-white font-semibold text-sm">6/10</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-slate-700 font-semibold w-32">Trust:</span>
                  <div className="flex-1 bg-white rounded-full h-8 overflow-hidden">
                    <div className="bg-teal-600 h-full flex items-center justify-end pr-3" style={{width: '70%'}}>
                      <span className="text-white font-semibold text-sm">7/10</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-slate-700 font-semibold w-32">Motivation:</span>
                  <div className="flex-1 bg-white rounded-full h-8 overflow-hidden">
                    <div className="bg-teal-600 h-full flex items-center justify-end pr-3" style={{width: '70%'}}>
                      <span className="text-white font-semibold text-sm">7/10</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-slate-700 font-semibold w-32">Frustration:</span>
                  <div className="flex-1 bg-white rounded-full h-8 overflow-hidden">
                    <div className="bg-red-500 h-full flex items-center justify-end pr-3" style={{width: '40%'}}>
                      <span className="text-white font-semibold text-sm">4/10</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white border-l-4 border-red-500 p-4 rounded">
                <p className="text-slate-700">
                  <strong>Main driver of frustration:</strong><br />
                  → Lack of guided prompts at first launch creates uncertainty.
                </p>
              </div>
            </div>
          </div>

          {/* 6. Opportunity Route */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold text-xl">
                6
              </div>
              <h3 className="text-3xl font-bold text-slate-900">Opportunity Route</h3>
            </div>
            <div className="bg-gradient-to-r from-teal-600 to-slate-700 text-white p-8 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Recommended single strategic angle for the biggest lift:</h4>
              <p className="text-2xl font-bold mb-6 text-teal-100">
                "Reduce first-session friction by 30% through guided prompts and micro-affirmations."
              </p>
              <div className="bg-white/10 p-6 rounded-lg">
                <h5 className="font-semibold mb-4">Expected impact:</h5>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <i className="ri-arrow-right-line"></i>
                    +12–18% Day-1 retention
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-arrow-right-line"></i>
                    +8–12% activation improvement
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-arrow-right-line"></i>
                    Higher perceived value
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-arrow-right-line"></i>
                    Higher conversion potential
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 7. One High-Impact Recommendation */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold text-xl">
                7
              </div>
              <h3 className="text-3xl font-bold text-slate-900">One High-Impact Recommendation</h3>
            </div>
            <div className="bg-amber-50 border-2 border-amber-300 p-8 rounded-xl">
              <h4 className="text-2xl font-bold text-slate-900 mb-4">Add a lightweight "Smart Start" guided overlay</h4>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  Only 3 screens
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  Shows users exactly what to do
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  Eliminates uncertainty
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  Increases value recognition speed
                </li>
              </ul>
            </div>
          </div>

          {/* End of Report */}
          <div className="bg-gradient-to-br from-slate-900 to-teal-900 text-white p-12 rounded-2xl text-center">
            <h3 className="text-3xl font-bold mb-6">End of Sample Report</h3>
            <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
              This preview demonstrates less than 10% of what you receive in a full evaluation.
            </p>
            <div className="bg-white/10 p-8 rounded-xl mb-8">
              <h4 className="text-xl font-semibold mb-6">Your actual report includes:</h4>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-400 mt-1"></i>
                  <span>Full intelligence scoring</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-400 mt-1"></i>
                  <span>Category benchmarks</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-400 mt-1"></i>
                  <span>Competitive distance mapping</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-400 mt-1"></i>
                  <span>Investor readiness</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-400 mt-1"></i>
                  <span>Passport certification</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-400 mt-1"></i>
                  <span>Growth projections</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-400 mt-1"></i>
                  <span>Market fit scoring</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-400 mt-1"></i>
                  <span>Strategic blueprint & upgrades</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/pricing"
                className="bg-teal-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-400 transition-all inline-flex items-center justify-center gap-2 whitespace-nowrap"
              >
                Start Your Evaluation
                <i className="ri-arrow-right-line"></i>
              </a>
              <button
                onClick={handleDownload}
                className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all inline-flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-download-line"></i>
                Download Sample
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
